import { atom, atomFamily, selector } from 'recoil';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';

/**
 * Cache modified structure sets by their DICOM UID.
 * Each StructureSet maintains its own state when switching between multiple loaded sets.
 */
export const structureSetStateFamily = atomFamily<
    StructureSet | undefined,
    string
>({
    key: 'structureSetById',
    default: undefined,
    dangerouslyAllowMutability: true,
});

export const structureSetReadModelFamily = atomFamily<
    StructureSetReadModel | undefined,
    string
>({
    key: 'structureSetReadModelById',
    default: undefined,
});

export const currentStructureSetIdState = atom<string | undefined>({
    key: 'currentStructureSetId',
    default: undefined,
});

export const currentStructureSetState = selector<
    StructureSetReadModel | undefined
>({
    key: 'currentStructureSetReadModel',
    get: ({ get }) => {
        const currentId = get(currentStructureSetIdState);
        return currentId
            ? get(structureSetReadModelFamily(currentId))
            : undefined;
    },
    set: ({ set }, newValue) => {
        if (newValue !== undefined) {
            const readModel = newValue as StructureSetReadModel;
            set(structureSetReadModelFamily(readModel.dicomUid), readModel);
            set(currentStructureSetIdState, readModel.dicomUid);
        } else {
            set(currentStructureSetIdState, undefined);
        }
    },
});

/**
 * True when the currently-selected structure set is not APPROVED. Drives
 * the "Inputs are unapproved" warning in workflow-entry modals (Dose+,
 * Adapt+, …). REJECTED and UNAPPROVED both warn; an absent (still-loading) set
 * does not trigger the warning.
 */
export const hasUnapprovedInputsState = selector<boolean>({
    key: 'hasUnapprovedInputs',
    get: ({ get }) => {
        const ss = get(currentStructureSetState);
        return !!ss && !ss.isApproved;
    },
});
